import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import AuthLayout from '../components/AuthLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Eye, EyeOff } from 'lucide-react';

const Login = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});
  
  const { signIn, isAuthenticated, loading, userProfile } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const from = location.state?.from?.pathname || '/dashboard';

  useEffect(() => {
    if (isAuthenticated && userProfile && !loading) {
      // Redirect based on user role
      if (userProfile.role === 'jobasti_admin') {
        navigate('/admin/dashboard', { replace: true });
      } else {
        navigate(from, { replace: true });
      }
    }
  }, [isAuthenticated, userProfile, loading, navigate, from]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.email) {
      newErrors.email = t('login.errEmailRequired');
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = t('login.errEmailInvalid');
    }
    
    if (!formData.password) {
      newErrors.password = t('login.errPasswordRequired');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    const { data, error } = await signIn(formData.email, formData.password);
    
    if (error) {
      setErrors({ form: error.message || t('login.errFailed') });
    } else if (data) {
      // Navigation handled by useEffect
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSubmit(e);
    }
  };

  return (
    <AuthLayout
      heroImage="https://images.unsplash.com/photo-1556761175-5973dc0f32e7?auto=format&fit=crop&w=1600&q=80"
      heroAlt="Modern team collaborating"
    >
      <h1 className="text-4xl font-serif font-medium text-navy-900 tracking-tight">
        <span className="text-blue-600">{t('login.title1')}</span> {t('login.title2')}
      </h1>
      <p className="text-sm text-gray-500 mt-3 mb-7">{t('login.subtitle')}</p>

      <form onSubmit={handleSubmit} className="space-y-4">
        {errors.form && (
          <Alert variant="destructive">
            <AlertDescription>{errors.form}</AlertDescription>
          </Alert>
        )}

        <div className="space-y-1.5">
          <Label htmlFor="email" className="text-sm text-gray-700">{t('common.email')}</Label>
          <Input
            id="email"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            placeholder={t('login.emailPlaceholder')}
            className={`h-11 ${errors.email ? 'border-red-500' : ''}`}
            data-testid="email-input"
          />
          {errors.email && <p className="text-xs text-red-500">{errors.email}</p>}
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="password" className="text-sm text-gray-700">{t('common.password')}</Label>
          <div className="relative">
            <Input
              id="password"
              name="password"
              type={showPassword ? 'text' : 'password'}
              value={formData.password}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              placeholder={t('login.passwordPlaceholder')}
              className={`h-11 pr-11 ${errors.password ? 'border-red-500' : ''}`}
              data-testid="password-input"
            />
            <button
              type="button"
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-gray-400 hover:text-gray-600 rounded"
              onClick={() => setShowPassword(!showPassword)}
              tabIndex={-1}
            >
              {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
          <div className="flex justify-end pt-1">
            <Link to="/forgot-password" className="text-xs text-blue-500 hover:text-blue-600 font-medium">
              {t('login.forgotPassword')}
            </Link>
          </div>
          {errors.password && <p className="text-xs text-red-500">{errors.password}</p>}
        </div>

        <Button
          type="submit"
          className="w-full h-12 bg-blue-500 hover:bg-blue-600 text-white font-semibold rounded-xl shadow-sm"
          disabled={loading}
          data-testid="login-button"
        >
          {loading ? t('common.signingIn') : t('common.signIn')}
        </Button>
      </form>

      <div className="mt-6 text-center">
        <p className="text-sm text-gray-600">
          {t('login.needAccount')}{' '}
          <Link to="/demo" className="text-blue-500 hover:text-blue-600 font-semibold">
            {t('login.contactUs')}
          </Link>
        </p>
        <p className="text-xs text-gray-400 mt-2">
          {t('login.areYouSeeker')}{' '}
          <Link to="/for-jobseekers" className="text-blue-500 hover:text-blue-600 font-medium">{t('login.browseJobs')}</Link>
        </p>
      </div>
    </AuthLayout>
  );
};

export default Login;